import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';

import { FormattedMessage } from '@flipster/i18n';
import Formatted from '@flipster/react/design-utils/formatted';
import { css } from '@flipster/styled-system/css';
import { center, stack } from '@flipster/styled-system/patterns';

import { SeparatorTitle } from '../../component/separator-title';
import { RegisterButton } from '../../component/tc-button';
import { competitionApiBunja, competitionBunja, competitionTabsBunja } from '../../state';
import { BriefInfo } from './brief-info';
import { competitionMilestoneDataSourceBunja } from './competition-data-source';
import { GaugeArea } from './gauge-area';
import { MilestoneDataSourceContext, milestoneBunja } from './state';

const pascalCaseValueMapper: Record<string, string> = {
  volume: 'Volume',
  pnl: 'P&L',
  roi: 'ROI',
};

export const Milestone = () => {
  const { dataSource } = useBunja(competitionMilestoneDataSourceBunja);

  return (
    <MilestoneDataSourceContext.Provider value={dataSource}>
      <MilestoneContent />
    </MilestoneDataSourceContext.Provider>
  );
};

const MilestoneContent = () => {
  const { isRegisteredAtom } = useBunja(competitionBunja);
  const { competitionStatusAtom } = useBunja(competitionApiBunja);
  const { selectedEventTabValueAtom } = useBunja(competitionTabsBunja);
  const { milestonesAtom, maxPrizePoolAtom } = useBunja(milestoneBunja);
  const competitionStatus = useAtomValue(competitionStatusAtom);
  const milestones = useAtomValue(milestonesAtom);
  const maxPrizePool = useAtomValue(maxPrizePoolAtom);
  const selectedEventTabValue = useAtomValue(selectedEventTabValueAtom);
  const isRegistered = useAtomValue(isRegisteredAtom);

  if (milestones.length === 0) return null;

  if (competitionStatus === 'UPCOMING')
    return (
      <section className={stack({ gap: { base: 40, tablet: 16 }, alignSelf: 'stretch' })}>
        <SeparatorTitle
          colorSet='normal'
          subtitle={`${pascalCaseValueMapper[selectedEventTabValue ?? '']} Milestones`}
          title={
            <FormattedMessage
              id='Competition.r251215.Trade_big_win_amount'
              values={{ amount: <Formatted.ShortNumber value={maxPrizePool} /> }}
            />
          }
        />
        <div
          className={stack({
            px: 24,
            pt: 32,
            pb: 54,
            gap: 64,
            rounded: 16,
          })}
        >
          <GaugeArea />
          <div className={center({ mt: { base: 40, tablet: 4 } })}>
            <RegisterButton buttonLocation='milestone' />
          </div>
        </div>
      </section>
    );

  return (
    <section
      className={stack({ gap: 32, alignSelf: 'stretch', alignItems: 'stretch', mt: 24, mb: { base: 80, tablet: 120 } })}
    >
      <p className={css({ textStyle: 'Title/S', color: 'content.default.level-5', textAlign: 'left' })}>
        {competitionStatus === 'ONGOING' ? (
          <FormattedMessage id='Competition.r251215.Increase_prize_pool_together' />
        ) : (
          <FormattedMessage id='Competition.r251215.Milestone_final_results' />
        )}
      </p>
      <BriefInfo milestoneType='default' />
      <GaugeArea />
      {competitionStatus === 'ONGOING' && !isRegistered && (
        <div className={center({ mt: { base: 40, tablet: 4 } })}>
          <RegisterButton buttonLocation='milestone' />
        </div>
      )}
    </section>
  );
};
